import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, Crown, Star, Rocket } from "lucide-react";
import { homesTiers, homesCheckout, homesMyAgentAccount } from "@/lib/api";
import { useAuth, login } from "@/context/AuthContext";

const ICONS = { free: Rocket, professional: Star, local_expert: Crown };

export default function AgentSubscribe() {
  const [tiers, setTiers] = useState(null);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    homesTiers().then((d) => setTiers(d.tiers)).catch(() => setTiers([]));
    if (user) homesMyAgentAccount().then((d) => setCurrent(d.account)).catch(() => {});
  }, [user]);

  const choose = async (tier) => {
    if (!user) { login("/homes/subscribe"); return; }
    if (tier.id === "free") { navigate("/homes/agent-portal"); return; }
    setBusy(tier.id);
    try {
      const { checkout_url } = await homesCheckout(tier.lookup_key);
      window.location.href = checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setBusy("");
    }
  };

  return (
    <div data-testid="agent-subscribe">
      <section className="bg-[#0F172A] text-white">
        <div className="mx-auto max-w-[1400px] px-4 py-14 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#93B4FF]">For Agents</span>
          <h1 className="mt-3 text-4xl font-black md:text-5xl">Grow your business on HomesPulse</h1>
          <p className="mx-auto mt-2 max-w-2xl text-white/70">Join the place-first real-estate network. Get a directory profile, a hosted microsite, and featured placement — powered by community intelligence.</p>
        </div>
      </section>

      <div className="mx-auto max-w-[1200px] px-4 py-12">
        {!tiers ? (
          <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></div>
        ) : (
          <div className="grid gap-6 md:grid-cols-3" data-testid="tiers-grid">
            {tiers.map((t) => {
              const Icon = ICONS[t.id] || Star;
              const active = current?.tier === t.id;
              const featured = t.id === "professional";
              return (
                <div key={t.id}
                  className={`relative flex flex-col rounded-3xl border-2 bg-white p-7 ${featured ? "border-[#2563EB] shadow-xl" : "border-slate-200"}`}
                  data-testid={`tier-card-${t.id}`}>
                  {featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#2563EB] px-3 py-1 text-xs font-bold text-white">Most popular</span>}
                  <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${featured ? "bg-[#2563EB] text-white" : "bg-[#EEF2FF] text-[#2563EB]"}`}><Icon className="h-5 w-5" /></div>
                  <h3 className="mt-4 text-2xl font-black text-[#0F172A]">{t.name}</h3>
                  <div className="mt-1 text-3xl font-black text-[#0F172A]">${t.price}<span className="text-base font-semibold text-slate-400">{t.price ? "/mo" : ""}</span></div>
                  <ul className="mt-5 flex-1 space-y-2.5">
                    {t.perks.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" /> {p}</li>
                    ))}
                  </ul>
                  <button onClick={() => choose(t)} disabled={busy === t.id || active}
                    className={`mt-6 flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition-colors disabled:opacity-60 ${featured ? "bg-[#2563EB] text-white hover:bg-[#1D4ED8]" : "border border-slate-300 text-[#0F172A] hover:bg-slate-50"}`}
                    data-testid={`tier-select-${t.id}`}>
                    {busy === t.id && <Loader2 className="h-4 w-4 animate-spin" />}
                    {active ? "Current plan" : t.price ? `Choose ${t.name}` : "Get started free"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-8 text-center text-xs text-slate-400">Test mode — use card 4242 4242 4242 4242, any future expiry & CVC. Cancel anytime.</p>
      </div>
    </div>
  );
}
